'use client'

import { type PropsWithChildren, useEffect, useMemo } from 'react'
import { cocoonPrivateRoutes } from '@cocoonspace/shared/config/cocoon-private-routes'

import { usePathname, useRouter } from '~/hooks/use-localized-navigation.hook'
import { stripLocaleFromPath } from '~/i18n/routing'
import { useAuth } from '~/providers/auth-provider'
import { useUserContext } from '~/providers/user-provider'

export const AuthLayout = ({ children }: PropsWithChildren) => {
	const router = useRouter()
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const { isAuth, isLoading, setIsAuthDialogOpen } = useAuth()
	const { accountQuery } = useUserContext()
	const account = accountQuery.data
	// The SDK does not declare the API's status field yet.
	const accountStatus =
		account && 'status' in account ? account.status : undefined

	useEffect(() => {
		if (pathname === '/account-unavailable') return

		if (accountStatus === 'blocked') {
			router.replace('/account-unavailable')
		}
	}, [accountStatus, pathname, router])

	const privateRouteConfig = useMemo(
		() => cocoonPrivateRoutes.find(({ path }) => path.test(pathname)),
		[pathname],
	)

	const shouldRenderChildren = useMemo(() => {
		if (!privateRouteConfig) return true // not a private route
		if (isLoading) return false // private route + loading → wait
		return isAuth // private route + not loading → render if auth
	}, [privateRouteConfig, isLoading, isAuth])

	useEffect(() => {
		if (!privateRouteConfig) return // not a private route, do nothing
		if (isLoading) return // still loading, wait
		if (isAuth) return // authenticated, do nothing

		// At this point: private route + not loading + not auth
		if (privateRouteConfig.mode === 'redirect') {
			router.push('/')
			return
		}

		setIsAuthDialogOpen(true)
		// oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
	}, [privateRouteConfig, isLoading, isAuth, setIsAuthDialogOpen])

	return shouldRenderChildren ? children : <div className='min-h-[80vh]' />
}
